/**
 * useHostListings Hook
 *
 * Fetches the current host's listings from Firestore.
 * Matches by hostId which uses the sanitized email format (e.g. gobnbnet_gmail_com).
 */

import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuth } from '../../../contexts/AuthContext';

export type ListingStatus = 'active' | 'paused' | 'draft' | 'pending';

export interface HostListing {
  id: string;
  title: string;
  propertyType: string;
  photo: string;
  photos: string[];
  location: {
    city: string;
    region: string;
  };
  status: ListingStatus;
  pricing: {
    nightlyRate: number;
    currency: string;
  };
  stats: {
    rating?: number;
    reviewCount?: number;
  };
  instantBooking: boolean;
  updatedAt: Date;
}

/**
 * Map Firestore listing status to host dashboard status
 */
function mapStatus(firestoreStatus: string): ListingStatus {
  switch (firestoreStatus) {
    case 'published':
      return 'active';
    case 'paused':
      return 'paused';
    case 'draft':
      return 'draft';
    case 'pending':
      return 'pending';
    default:
      return 'draft';
  }
}

/**
 * Convert email to the sanitized ID format used in Firestore
 * e.g. "gobnbnet@gmail.com" -> "gobnbnet_gmail_com"
 */
function emailToHostId(email: string): string {
  return email.toLowerCase().replace(/[^a-z0-9]/g, '_');
}

export const useHostListings = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState<HostListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.email) {
      setListings([]);
      setIsLoading(false);
      return;
    }

    async function fetchListings() {
      setIsLoading(true);
      try {
        const listingsRef = collection(db, 'listings');
        const snapshot = await getDocs(listingsRef);

        // Build set of possible host IDs for this user:
        // 1. Firebase Auth UID (e.g. "sdhLHkinafdYi22uC2XsHTjCtuq1")
        // 2. Sanitized email format (e.g. "gobnbnet_gmail_com")
        const possibleHostIds = new Set<string>();
        possibleHostIds.add(user!.uid);
        possibleHostIds.add(emailToHostId(user!.email));

        const matchingDocs = snapshot.docs.filter((doc) => {
          const data = doc.data();
          const hostId = data.hostId || '';
          return possibleHostIds.has(hostId);
        });

        const hostListings: HostListing[] = matchingDocs.map((doc) => {
          const data = doc.data();
          const photos = data.photos || [];
          const firstPhoto = photos.length > 0 ? photos[0].url : '';

          return {
            id: doc.id,
            title: data.title || '',
            propertyType: data.propertyType || '',
            photo: firstPhoto,
            photos: photos.map((p: { url: string }) => p.url),
            location: {
              city: data.location?.city || '',
              region: data.location?.state || '',
            },
            status: mapStatus(data.status),
            pricing: {
              nightlyRate: data.pricing?.nightlyRate || 0,
              currency: data.pricing?.currency || 'USD',
            },
            stats: {
              rating: data.rating?.average || undefined,
              reviewCount: data.rating?.count || undefined,
            },
            instantBooking: data.instantBook || false,
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
          };
        });

        // Sort: active first, then by title
        hostListings.sort((a, b) => {
          const statusOrder: Record<ListingStatus, number> = { active: 0, pending: 1, paused: 2, draft: 3 };
          const statusDiff = statusOrder[a.status] - statusOrder[b.status];
          if (statusDiff !== 0) return statusDiff;
          return a.title.localeCompare(b.title);
        });

        setListings(hostListings);
      } catch (err) {
        console.error('Failed to fetch host listings:', err);
        setListings([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchListings();
  }, [user?.uid, user?.email]);

  const activeCount = listings.filter((l) => l.status === 'active').length;
  const pausedCount = listings.filter((l) => l.status === 'paused').length;
  const draftCount = listings.filter((l) => l.status === 'draft').length;

  return { listings, isLoading, counts: { active: activeCount, paused: pausedCount, draft: draftCount } };
};

export default useHostListings;
